import React, { useEffect, useState } from 'react';
import { attestationService } from '../../services/attestationService';
import { AlertBanner } from '@flowcore-water/ui';
import { useAuth } from '../../contexts/AuthContext';
import type { Attestation } from '../../types';
import { useTheme } from '@flowcore-water/ui';

type FilterStatus = 'all' | 'pending' | 'attested';

export const AttestationDashboard: React.FC = () => {
  const { t } = useTheme();
  const { user } = useAuth();
  const isReadOnly = user?.role === 'outside_observer';
  const [attestations, setAttestations] = useState<Attestation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');

  useEffect(() => {
    const loadAttestations = async () => {
      try {
        setLoading(true);
        const data = await attestationService.getAll();
        setAttestations(data);
        setError(null);
      } catch (err) {
        console.error('Error loading attestations:', err);
        setError('Failed to load attestations');
      } finally {
        setLoading(false);
      }
    };

    loadAttestations();
  }, []);

  const handleDismiss = async (attestationId: string) => {
    if (!confirm('Dismiss this attestation requirement? This cannot be undone.')) return;
    try {
      await attestationService.dismiss(attestationId, user!.uid);
      setAttestations((prev) => prev.filter((att) => att.id !== attestationId));
    } catch (err: any) {
      console.error('Error dismissing attestation:', err);
      setError(`Dismiss failed: ${err.message || err}. Make sure Firestore rules are deployed.`);
    }
  };

  const handleDismissForArticle = async (articleId: string, _articleVersion: number) => {
    const pending = attestations.filter(
      (att) => att.articleId === articleId && !att.attested
    );
    if (pending.length === 0) return;
    if (!confirm(`Dismiss all ${pending.length} pending attestation(s) for this article? This cannot be undone.`)) return;
    try {
      await attestationService.dismissForArticle(articleId, user!.uid);
      setAttestations((prev) =>
        prev.filter((att) => !(att.articleId === articleId && !att.attested))
      );
    } catch (err: any) {
      console.error('Error bulk-dismissing attestations:', err);
      setError(`Bulk dismiss failed: ${err.message || err}. Make sure Firestore rules are deployed.`);
    }
  };

  const totalCount = attestations.length;
  const pendingCount = attestations.filter((att) => !att.attested).length;
  const attestedCount = attestations.filter((att) => att.attested).length;

  const filteredAttestations = attestations.filter((att) => {
    if (filterStatus === 'pending') return !att.attested;
    if (filterStatus === 'attested') return att.attested;
    return true;
  });

  const articleGroups = new Map<
    string,
    {
      articleId: string;
      title: string;
      version: number;
      total: number;
      pending: number;
      attested: number;
    }
  >();

  attestations.forEach((att) => {
    const key = att.articleId;
    if (!articleGroups.has(key)) {
      articleGroups.set(key, {
        articleId: att.articleId,
        title: att.articleTitle,
        version: att.articleVersion,
        total: 0,
        pending: 0,
        attested: 0,
      });
    }
    const group = articleGroups.get(key)!;
    // Keep the highest version seen
    if (att.articleVersion > group.version) group.version = att.articleVersion;
    group.total++;
    if (att.attested) {
      group.attested++;
    } else {
      group.pending++;
    }
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="" style={{ color: t.textMuted }}>Loading compliance dashboard...</div>
      </div>
    );
  }

  if (error) {
    return <AlertBanner variant="error" message={error} />;
  }

  return (
    <div className="space-y-8">
      {error && <AlertBanner variant="error" message={error} onDismiss={() => setError(null)} />}
      <div>
        <h1 className="text-3xl font-bold " style={{ color: t.textPrimary }}>
          Compliance Dashboard
        </h1>
        <p className="mt-2 " style={{ color: t.textMuted }}>
          Track training attestation status across the team
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-lg border   p-6">
          <p className="text-sm font-medium  uppercase tracking-wide" style={{ color: t.textMuted }}>
            Total Attestations
          </p>
          <p className="mt-2 text-4xl font-bold " style={{ color: t.textPrimary }}>
            {totalCount}
          </p>
        </div>

        <div className="rounded-lg border border-amber-700 bg-amber-900/20 p-6">
          <p className="text-sm font-medium text-amber-400 uppercase tracking-wide">
            Pending
          </p>
          <p className="mt-2 text-4xl font-bold text-amber-400">
            {pendingCount}
          </p>
        </div>

        <div className="rounded-lg border border-green-700 bg-green-900/20 p-6">
          <p className="text-sm font-medium  uppercase tracking-wide" style={{ color: t.success }}>
            Attested
          </p>
          <p className="mt-2 text-4xl font-bold " style={{ color: t.success }}>
            {attestedCount}
          </p>
        </div>
      </div>

      {/* Article Progress Bars */}
      {articleGroups.size > 0 && (
        <div className="space-y-4">
          <h2 className="text-xl font-bold " style={{ color: t.textPrimary }}>
            Attestation Progress by Article
          </h2>
          <div className="space-y-4">
            {Array.from(articleGroups.entries()).map(([key, group]) => {
              const progressPercent = (group.attested / group.total) * 100;

              return (
                <div key={key} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="font-semibold " style={{ color: t.textSecondary }}>
                      {group.title} (v{group.version})
                    </p>
                    <div className="flex items-center gap-3">
                      <span className="text-sm " style={{ color: t.textMuted }}>
                        {group.attested} / {group.total}
                      </span>
                      {group.pending > 0 && !isReadOnly && (
                        <button
                          onClick={() => handleDismissForArticle(group.articleId, group.version)}
                          className="text-xs hover:opacity-80 transition-colors" style={{ color: t.fail }}
                        >
                          Dismiss remaining ({group.pending})
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="w-full h-3 rounded-full  overflow-hidden border ">
                    <div
                      className="h-full bg-gradient-to-r from-green-600 to-cyan-600 transition-all"
                      style={{ width: `${progressPercent}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Filter and Table */}
      <div className="space-y-4">
        <div className="flex gap-2">
          <button
            onClick={() => setFilterStatus('all')}
            className="px-4 py-2 rounded font-medium transition-colors"
            style={filterStatus === 'all'
              ? { backgroundColor: t.accentDim, color: t.textPrimary }
              : { color: t.textSecondary }}
          >
            All
          </button>
          <button
            onClick={() => setFilterStatus('pending')}
            className="px-4 py-2 rounded font-medium transition-colors"
            style={filterStatus === 'pending'
              ? { backgroundColor: '#d97706', color: t.textPrimary }
              : { color: t.textSecondary }}
          >
            Pending
          </button>
          <button
            onClick={() => setFilterStatus('attested')}
            className="px-4 py-2 rounded font-medium transition-colors"
            style={filterStatus === 'attested'
              ? { backgroundColor: t.success, color: t.textPrimary }
              : { color: t.textSecondary }}
          >
            Attested
          </button>
        </div>

        <div className="rounded-lg border   overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b  ">
                <th className="px-6 py-3 text-left font-semibold " style={{ color: t.textSecondary }}>
                  User
                </th>
                <th className="px-6 py-3 text-left font-semibold " style={{ color: t.textSecondary }}>
                  Article
                </th>
                <th className="px-6 py-3 text-left font-semibold " style={{ color: t.textSecondary }}>
                  Version
                </th>
                <th className="px-6 py-3 text-left font-semibold " style={{ color: t.textSecondary }}>
                  Status
                </th>
                <th className="px-6 py-3 text-left font-semibold " style={{ color: t.textSecondary }}>
                  Date
                </th>
                <th className="px-6 py-3 text-left font-semibold " style={{ color: t.textSecondary }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredAttestations.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-6 py-8 text-center " style={{ color: t.textMuted }}
                  >
                    No attestations found
                  </td>
                </tr>
              ) : (
                filteredAttestations.map((att) => (
                  <tr
                    key={att.id}
                    className="border-b  hover: transition-colors"
                  >
                    <td className="px-6 py-4 " style={{ color: t.textPrimary }}>
                      {att.userName || att.userEmail || 'Unknown'}
                    </td>
                    <td className="px-6 py-4 " style={{ color: t.textSecondary }}>
                      {att.articleTitle}
                    </td>
                    <td className="px-6 py-4 " style={{ color: t.textMuted }}>
                      {att.articleVersion}
                    </td>
                    <td className="px-6 py-4">
                      {att.attested ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-green-900/30 px-3 py-1 font-medium  border border-green-700/50" style={{ color: t.success }}>
                          ✓ Attested
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-900/30 px-3 py-1 font-medium text-amber-400 border border-amber-700/50">
                          ⏳ Pending
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 " style={{ color: t.textMuted }}>
                      {att.attestedAt
                        ? new Date(att.attestedAt).toLocaleDateString()
                        : new Date(att.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      {!att.attested && !isReadOnly && (
                        <button
                          onClick={() => handleDismiss(att.id)}
                          className="text-xs hover:opacity-80 transition-colors" style={{ color: t.fail }}
                        >
                          Dismiss
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
